/**
 * Como se lee una solicitud en pantalla. Aqui vive el formato (fechas, horas,
 * que fila tiene valor y en que orden va), separado de los componentes, que
 * solo pintan lo que reciben.
 */
import moment from "moment";

import { nombreCompleto } from "./solicitud";
import { TIPO_ARIA, VISTA_DETALLE } from "../constants";

/**
 * Nivel de ARIA como se lee en el select: el nombre y cuanto dura, que es
 * lo que decide si cabe en la franja que se pidio.
 *
 * "Curso y taller de Normas APA (90 min)"
 */
export const nivelConDuracion = ({ nombre, duracion }) =>
  duracion ? `${nombre} (${duracion} min)` : nombre;

/** "Nivel 1 · Exploratorio", o "" si el evento no tiene nivel. */
export const etiquetaNivel = ({ numeroNivel, nivel }) =>
  [numeroNivel ? `Nivel ${numeroNivel}` : "", nivel].filter(Boolean).join(" · ");

/**
 * Los eventos de ARIA agrupados por nivel, en orden (los que no tienen
 * nivel al final): [{ etiqueta, eventos }].
 */
export const nivelesAgrupados = (niveles) => {
  const grupos = [];
  [...niveles]
    .sort((a, b) => (a.numeroNivel ?? 99) - (b.numeroNivel ?? 99))
    .forEach((evento) => {
      const etiqueta = etiquetaNivel(evento) || "Otros";
      const grupo = grupos.find((g) => g.etiqueta === etiqueta);
      if (grupo) grupo.eventos.push(evento);
      else grupos.push({ etiqueta, eventos: [evento] });
    });
  return grupos;
};

/** La segunda linea de un evento elegido: "Nivel 1 · Exploratorio · 60 min — de que trata". */
export const detalleDeNivel = (evento) => {
  const datos = [etiquetaNivel(evento), evento.duracion ? `${evento.duracion} min` : ""]
    .filter(Boolean)
    .join(" · ");
  return [datos, evento.descripcion].filter(Boolean).join(" — ");
};

/**
 * Cuanto suman las duraciones de una lista de niveles, en un texto que se
 * pueda comparar con la franja pedida: 195 -> "3 h 15 min".
 *
 * Devuelve "" si no hay nada que sumar, para no pintar un total en cero.
 */
export const duracionTotal = (niveles) => {
  const minutos = (niveles ?? []).reduce(
    (suma, { duracion }) => suma + (Number(duracion) || 0),
    0,
  );
  if (!minutos) return "";

  const horas = Math.floor(minutos / 60);
  const sueltos = minutos % 60;

  if (!horas) return `${sueltos} min`;
  return sueltos ? `${horas} h ${sueltos} min` : `${horas} h`;
};

/** "08:00:00" -> "08:00" */
export const hora = (valor) => (valor ? valor.slice(0, 5) : "");

/** Fecha y hora de registro, tal como las devuelve el backend. */
export const momento = (valor) =>
  valor ? moment(valor).format("YYYY-MM-DD, h:mm a") : "";

const materiaYGrupo = ({ materia, grupo }) =>
  materia ? [materia, grupo].filter(Boolean).join(" / ") : "";

// El docente reserva una franja; el estudiante solo propone un dia, asi que
// sin horas se muestra la fecha sola.
const jornada = ({ fecha_prestamo, hora_inicio, hora_fin }) => {
  if (!fecha_prestamo) return "";

  const franja = [hora(hora_inicio), hora(hora_fin)].filter(Boolean).join(" a ");
  return franja ? `${fecha_prestamo}, ${franja}` : fecha_prestamo;
};

// Una fecha sin franja es la que propuso el estudiante: todavia no esta
// confirmada por biblioteca.
const etiquetaFecha = ({ hora_inicio, hora_fin }) =>
  hora_inicio || hora_fin ? "Fecha" : "Fecha propuesta";

const lugar = ({ bloque, salon }) =>
  [bloque?.nombre, salon?.nombre].filter(Boolean).join(" - ");

const conValor = ([, valor]) => Boolean(valor);

/** Fila principal de la tabla de Procesos: todas son propias, va el tipo. */
export const filaPrincipalProcesos = ({ tipo_solicitud }) => tipo_solicitud?.nombre ?? "";

/**
 * Columnas de la tabla de Procesos del solicitante, en el formato de
 * ListarDatos (la misma tabla de Gestion): lo minimo para reconocer la
 * solicitud. La ARIA de un estudiante no trae materia ni lugar, y su fecha es
 * la propuesta; las columnas vacias no se pintan.
 */
export const COLUMNAS_PROCESOS = [
  { id: "estado", mostrar: ({ estado_actual }) => estado_actual?.nombre ?? "" },
  { id: "periodo", nombre: "Periodo: ", mostrar: ({ periodo }) => periodo ?? "" },
  { id: "materia", nombre: "Materia: ", mostrar: materiaYGrupo },
  {
    id: "fecha",
    // La etiqueta cambia: "Fecha" o "Fecha propuesta" (sin horas).
    mostrar: (solicitud) => {
      const cuando = jornada(solicitud);
      return cuando ? `${etiquetaFecha(solicitud)}: ${cuando}` : "";
    },
  },
  { id: "lugar", nombre: "Lugar: ", mostrar: lugar },
  { id: "registro", nombre: "Registrada: ", mostrar: ({ fecha_registro }) => momento(fecha_registro) },
];

/** Filas del detalle: lo basico de la solicitud. */
export const filasDetalle = (solicitud) =>
  [
    ["Periodo", solicitud.periodo],
    ["Materia", materiaYGrupo(solicitud)],
    ["Programa", solicitud.programa],
    [etiquetaFecha(solicitud), jornada(solicitud)],
    ["Lugar", lugar(solicitud)],
    ["Celular", solicitud.celular],
  ].filter(conValor);

/**
 * Filas de "¡ Informacion Basica !" en la pantalla de detalle: quien pidio
 * que, en que va y cuando se radico. Los estudiantes, los libros y el
 * historial no van aqui: se ven en "¡ Mas Informacion !".
 */
export const filasInformacionBasica = (solicitud) =>
  [
    ["Solicitante", nombreCompleto(solicitud.solicitante)],
    ["Tipo de solicitud", solicitud.tipo_solicitud?.nombre],
    ["Estado de la solicitud", solicitud.estado_actual?.nombre],
    ...filasDetalle(solicitud),
    // ARIA: lo define biblioteca al revisar.
    ["Recurso", solicitud.recurso?.nombre],
    [
      "Lugar de la capacitación",
      lugar({ bloque: solicitud.bloque_capacitacion, salon: solicitud.salon_capacitacion }),
    ],
    // Libro a tu clase: lo registra biblioteca al preparar.
    ["Peso de los libros", solicitud.peso ? `${solicitud.peso} g` : ""],
    ["Fecha de registro", momento(solicitud.fecha_registro)],
  ].filter(conValor);


/**
 * Lo que muestra cada pestaña de detalle, como lista { principal, secundario }:
 * asi un solo componente pinta todas. La usan Mas informacion (solicitante) y
 * el modal Abrir de Gestion.
 */
export const itemsDeDetalle = (vista, solicitud) => {
  const {
    tipo_solicitud: tipo,
    // El backend siempre manda las listas; el valor por defecto es solo para
    // que un cambio alla no tumbe la pantalla.
    estudiantes = [],
    libros = [],
    niveles = [],
    ejemplares = [],
    ejemplares_retirados: retirados = [],
    auxiliares = [],
    estados = [],
  } = solicitud;

  if (vista === VISTA_DETALLE.ESTUDIANTES) {
    return estudiantes.map(({ nombre, identificacion }) => ({
      principal: nombre,
      secundario: identificacion,
    }));
  }

  if (vista === VISTA_DETALLE.MATERIAL) {
    // Cada tipo trae su propia lista: la del otro llega vacia.
    return tipo?.codigo === TIPO_ARIA
      ? niveles.map(({ nivel, duracion }) => ({
          principal: nivel?.nombre,
          secundario: duracion ? `Duracion: ${duracion} minutos` : null,
        }))
      : libros.map(({ nombre }) => ({ principal: nombre }));
  }

  if (vista === VISTA_DETALLE.EJEMPLARES) {
    // Los retirados van al final, marcados, con quien y por que.
    return [
      ...ejemplares.map(({ titulo, codigo_barras, estudiante }) => ({
        principal: titulo,
        secundario: `Código ${codigo_barras} · ${estudiante ? `Prestado a ${estudiante.nombre}` : "Sin asignar"}`,
      })),
      ...retirados.map(({ titulo, codigo_barras, fecha_elimina, retirado_por, nota_retiro }) => ({
        principal: `${titulo} (retirado)`,
        secundario: [
          `Código ${codigo_barras}`,
          momento(fecha_elimina),
          retirado_por && `por ${retirado_por}`,
          nota_retiro && `Motivo: ${nota_retiro}`,
        ]
          .filter(Boolean)
          .join(" · "),
        retirado: true,
      })),
    ];
  }

  if (vista === VISTA_DETALLE.AUXILIARES) {
    return auxiliares.map(({ auxiliar, accion_nombre, fecha_registro }) => ({
      principal: auxiliar?.nombre,
      secundario: `${accion_nombre} · desde ${momento(fecha_registro)}`,
    }));
  }

  return estados.map(({ tipo_estado, fecha_registro, usuario_registro, mensaje }) => ({
    principal: tipo_estado?.nombre,
    secundario: [momento(fecha_registro), nombreCompleto(usuario_registro), mensaje]
      .filter(Boolean)
      .join(" - "),
  }));
};
